import { getAuthModule, HttpError } from '@littlebird/auth-client';
import {
  createProjectFileUploadUrl,
  deleteProjectFile as deleteProjectFileRequest,
  fetchProjectFiles,
  finalizeProjectFile,
  getSDK,
  type ProjectFile,
} from '@littlebird/sdk';
import { queryOptions } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { queryClient } from '@/query/client';

export type { ProjectFile } from '@littlebird/sdk';

const PROJECT_FILES_KEYS = {
  all: ['project-files'] as const,
  list: (projectId: string) => [...PROJECT_FILES_KEYS.all, projectId] as const,
};

const POLL_WHILE_PROCESSING = 4_000;

const hasProcessingFiles = (projectId: string): boolean => {
  const files = queryClient.getQueryData<ProjectFile[]>(
    PROJECT_FILES_KEYS.list(projectId)
  );
  return (
    files?.some(
      (file) =>
        file.ingestionStatus === 'pending' ||
        file.ingestionStatus === 'processing'
    ) ?? false
  );
};

export const projectFilesQuery = (projectId: string) =>
  queryOptions({
    enabled: getAuthModule().tokenManager.hasValidToken(),
    queryKey: PROJECT_FILES_KEYS.list(projectId),
    staleTime: 1000 * 30,
    // Poll while a file is still extracting/summarizing so the UI reaches a terminal state.
    refetchInterval: () =>
      hasProcessingFiles(projectId) ? POLL_WHILE_PROCESSING : false,
    queryFn: () => fetchProjectFiles(getSDK(), projectId),
  });

export const invalidateProjectFiles = (projectId: string) => {
  queryClient.invalidateQueries({
    queryKey: PROJECT_FILES_KEYS.list(projectId),
  });
};

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpError) {
    const body = error.body as
      | { detail?: { user_error_message?: string } }
      | undefined;
    return body?.detail?.user_error_message ?? fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

export function appendExtensionToFilenameStem(filename: string): string {
  const extensionIndex = filename.lastIndexOf('.');
  if (extensionIndex <= 0 || extensionIndex === filename.length - 1) {
    return filename;
  }
  const extension = filename.slice(extensionIndex + 1);
  return `${filename.slice(0, extensionIndex)}-${extension}.${extension}`;
}

/**
 * Two-step presigned upload mirroring the chat-attachment flow:
 * request a presigned URL, PUT bytes to S3, then finalize (which kicks off ingestion).
 */
async function uploadProjectFile(projectId: string, file: File): Promise<void> {
  const fileId = uuidv4();
  const contentType = file.type || 'application/octet-stream';

  const uploadUrl = await createProjectFileUploadUrl(getSDK(), projectId, {
    id: fileId,
    filename: appendExtensionToFilenameStem(file.name),
    contentType,
    size: file.size,
  });

  const uploadResult = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!uploadResult.ok) {
    throw new Error(`Upload failed with status ${uploadResult.status}`);
  }

  await finalizeProjectFile(getSDK(), projectId, fileId);
}

export const uploadProjectFileMutation = (projectId: string) => ({
  mutationKey: ['upload-project-file', projectId],
  mutationFn: async (file: File) => {
    try {
      await uploadProjectFile(projectId, file);
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new Error(
        extractErrorMessage(error, `Failed to upload ${file.name}`)
      );
    }
  },
  onSuccess: () => invalidateProjectFiles(projectId),
});

export const deleteProjectFileMutation = (projectId: string) => ({
  mutationKey: ['delete-project-file', projectId],
  mutationFn: (fileId: string) =>
    deleteProjectFileRequest(getSDK(), projectId, fileId),
  onSuccess: () => invalidateProjectFiles(projectId),
});
